import { useNavigate, useLocation } from 'react-router-dom';

import { useTranslation } from '../hooks/useTranslation';
import { useKioskStore } from '../store/kioskStore';
import { useWalkUpStore } from '../store/walkUpStore';

/**
 * Floating "Home" button. Rendered globally on every screen except the
 * welcome/login/payment-waiting screens where returning home would cancel
 * in-flight work (or isn't needed). Always ends any active kiosk session
 * and clears the walk-up cart so the next user starts clean.
 */
export function HomeButton(): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const endKioskSession = useKioskStore((s) => s.endSession);
  const resetWalkUp = useWalkUpStore((s) => s.reset);

  // Hide on these routes
  const path = location.pathname;
  const hidden =
    path === '/' ||
    path === '/login' ||
    path === '/photos/payment';

  if (hidden) return null;

  const handleHome = () => {
    endKioskSession();
    resetWalkUp();
    navigate('/');
  };

  return (
    <button
      type="button"
      onClick={handleHome}
      aria-label={t.common.homeAriaLabel}
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 h-12 px-5 rounded-full bg-white/[0.08] hover:bg-white/[0.14] active:bg-white/20 border border-white/15 backdrop-blur text-white text-sm font-bold cursor-pointer transition-all shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
    >
      <span className="text-base leading-none" aria-hidden>
        🏠
      </span>
      <span className="tracking-wide">{t.common.homeLabel}</span>
    </button>
  );
}
